import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { SerializedError } from "../shared/models/errorHandlingModels";
import { NotificationMessage } from '../shared/models/models';
import { Country } from '../common/models/CommonModels';
import { getCountries } from '../common/api/commonApi';
import { ConfirmRegister } from '../customer/shared/models/customerModels';
import customersApi from '../customer/shared/api/customersApi';
import { serializeError, createErrorNotification } from '../shared/utils/errorUtils';
import { isRegistrationHostRefusal } from './pilotHostGate';

interface ConfirmRegistrationState {
  isLoading: boolean;
  formNotificationMessage?: NotificationMessage | null;
  showPassword: boolean;
  showConfirmPassword: boolean;
  countries: Country[];

  /**
   * COM-REGISTRATION-INVITE-ONLY (HOST_POLICY_SPEC §6 FE-B) — the server refused company
   * self-registration on this host (403 `REGISTRATION_NOT_AVAILABLE_ON_HOST`). The page renders the
   * invite-only content IN PLACE (Codex C-1 — navigating to /account/sign-up would re-run the
   * client-only host check and could land on another registration form), instead of showing a red
   * form error the user can do nothing about.
   */
  registrationNotAvailableOnHost: boolean;
}

const initialState: ConfirmRegistrationState = {
  isLoading: false,
  showPassword: false,
  showConfirmPassword: false,
  countries: [],
  registrationNotAvailableOnHost: false,
};

export const loadCountries = createAsyncThunk(
  'confirmRegistration/loadCountries',
  async (_, { rejectWithValue }) => {
    try {
      const countries = await getCountries();
      return countries;
    } catch (error: any) {
      return rejectWithValue(serializeError(error));
    }
  }
);

export const confirmRegistration = createAsyncThunk(
  'confirmRegistration/confirmRegistration',
  async (model: ConfirmRegister, { rejectWithValue }) => {
    try {
      const result = await customersApi.confirmRegister(model);
      return result;
    } catch (error: any) {
      return rejectWithValue(serializeError(error));
    }
  }
);

const confirmRegistrationSlice = createSlice({
  name: 'confirmRegistration',
  initialState,
  reducers: {
    setFormNotificationMessage: (state, action: PayloadAction<NotificationMessage | undefined>) => {
      state.formNotificationMessage = action.payload;
    },
    togglePasswordVisibility: (state) => {
      state.showPassword = !state.showPassword;
    },
    toggleConfirmPasswordVisibility: (state) => {
      state.showConfirmPassword = !state.showConfirmPassword;
    },
    resetState: (state) => {
      state.isLoading = false;
      state.formNotificationMessage = null;
      state.showPassword = false;
      state.showConfirmPassword = false;
      state.registrationNotAvailableOnHost = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadCountries.pending, (state) => {
        state.isLoading = true;
        state.formNotificationMessage = null;
      })
      .addCase(loadCountries.fulfilled, (state, action) => {
        state.isLoading = false;
        state.countries = action.payload;
      })
      .addCase(loadCountries.rejected, (state, action) => {
        state.isLoading = false;
        state.formNotificationMessage = createErrorNotification(action.payload as SerializedError);
      })

      .addCase(confirmRegistration.pending, (state) => {
        state.isLoading = true;
        state.formNotificationMessage = null;
      })
      .addCase(confirmRegistration.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(confirmRegistration.rejected, (state, action) => {
        state.isLoading = false;

        const error = action.payload as SerializedError;
        if (isRegistrationHostRefusal(error?.code)) {
          // Not a form error — registration is not available on this host at all.
          state.registrationNotAvailableOnHost = true;
          state.formNotificationMessage = null;
          return;
        }

        state.formNotificationMessage = createErrorNotification(error);
      });
  },
});

export const {
  setFormNotificationMessage,
  togglePasswordVisibility,
  toggleConfirmPasswordVisibility,
  resetState,
} = confirmRegistrationSlice.actions;

export default confirmRegistrationSlice.reducer; 